/**
 * ComparisonPicker — the right pill that appears next to PeriodSelector when
 * comparison is active. Implements the dropdown from image 3:
 *
 *   Disable comparison        ← removes ?comparison from the URL
 *   Previous period (default)
 *   Year over year
 *   Custom period             ← reveals an inline date-range mini-picker
 *   ──────────────
 *   Match day of week         ← alignment modifier (default for prev / yoy)
 *   Match exact date          ← alignment modifier (no shift)
 *
 * Only one of the bottom two alignment modifiers can be on at a time; they pair
 * with whichever of the top three modes is active. They're shown as toggleable
 * rows with a checkmark for the currently-selected alignment.
 *
 * URL state owned here:
 *   ?comparison=<previous_period | yoy | custom>   (omitted = none)
 *   ?comparison_match=<day_of_week | exact_date>   (omitted = default: day_of_week)
 *   ?comparison_start, ?comparison_end             (only when comparison=custom)
 *
 * Rendered by AnalyticsPage only when ?comparison= is present in the URL.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Check } from 'lucide-react';
import DatePickerInput from './DatePickerInput';

const MODE_LABELS = {
	previous_period: 'Previous period',
	yoy: 'Year over year',
	custom: 'Custom period',
};

const ComparisonPicker = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const comparison = searchParams.get('comparison') || 'previous_period';
	const comparisonMatch = searchParams.get('comparison_match') || 'day_of_week';
	const comparisonStart = searchParams.get('comparison_start') || '';
	const comparisonEnd = searchParams.get('comparison_end') || '';

	const [open, setOpen] = useState(false);
	const [showCustomInline, setShowCustomInline] = useState(comparison === 'custom');
	const [customStart, setCustomStart] = useState(comparisonStart);
	const [customEnd, setCustomEnd] = useState(comparisonEnd);
	const wrapRef = useRef(null);

	// Close the dropdown when clicking anywhere outside its wrap (matches the
	// behavior of the existing filter dropdown elsewhere in the app).
	useEffect(() => {
		if (!open) return;
		const handleClickOutside = (e) => {
			if (!wrapRef.current || wrapRef.current.contains(e.target)) return;
			// The DatePickerInput popover (rendered via Radix Portal to document.body)
			// is logically a child of THIS dropdown, but lives outside wrapRef in the
			// DOM tree. Without this exemption, picking a day in the calendar would
			// close the entire comparison dropdown before the user sees the selection.
			if (e.target.closest && e.target.closest('.dpi-popover')) return;
			setOpen(false);
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	// Helper that pushes a new URLSearchParams to the route, preserving every
	// non-comparison param (period, filters, etc.).
	const updateUrl = (mutator) => {
		const next = new URLSearchParams(searchParams);
		mutator(next);
		navigate(`?${next.toString()}`, { replace: true });
	};

	const setMode = (mode) => {
		updateUrl(params => {
			params.set('comparison', mode);
			if (mode !== 'custom') {
				params.delete('comparison_start');
				params.delete('comparison_end');
			}
		});
		setShowCustomInline(mode === 'custom');
		// Don't auto-close the menu when picking "Custom period" — the user still
		// needs to fill the date inputs that just appeared. For the other modes,
		// closing is the expected one-shot behavior.
		if (mode !== 'custom') setOpen(false);
	};

	const setMatch = (match) => {
		updateUrl(params => {
			// Don't bother writing the default — keeps the URL clean.
			if (match === 'day_of_week') params.delete('comparison_match');
			else params.set('comparison_match', match);
		});
	};

	const disableComparison = () => {
		updateUrl(params => {
			params.delete('comparison');
			params.delete('comparison_match');
			params.delete('comparison_start');
			params.delete('comparison_end');
		});
		setOpen(false);
	};

	const applyCustomDates = () => {
		if (!customStart || !customEnd) return;
		updateUrl(params => {
			params.set('comparison', 'custom');
			params.set('comparison_start', customStart);
			params.set('comparison_end', customEnd);
		});
		setOpen(false);
	};

	// Pill label reflects the current mode. For "Custom period" we surface the
	// actual date range (matches the left PeriodSelector pill's behavior when
	// Custom Range is picked, e.g. "2025-07-01 → 2026-05-27") so the user can
	// read what window they're comparing against without re-opening the dropdown.
	// Falls back to the mode name when dates haven't been chosen yet.
	const pillLabel = (() => {
		if (comparison === 'custom' && comparisonStart && comparisonEnd) {
			return `${comparisonStart} → ${comparisonEnd}`;
		}
		return MODE_LABELS[comparison] || 'Previous period';
	})();

	return (
		<div className="comparison-picker" ref={wrapRef}>
			<button
				type="button"
				className="period-pill comparison-pill"
				onClick={() => setOpen(o => !o)}
			>
				<Calendar size={14} className="period-pill__icon" />
				<span className="period-pill__label">{pillLabel}</span>
				<svg width="10" height="10" viewBox="0 0 12 12" className="period-pill__chev">
					<path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
				</svg>
			</button>

			{open && (
				<div className="comparison-dropdown">
					<button
						type="button"
						className="comparison-dropdown__item comparison-dropdown__item--danger"
						onClick={disableComparison}
					>
						Disable comparison
					</button>

					{['previous_period', 'yoy', 'custom'].map(mode => (
						<button
							key={mode}
							type="button"
							className={`comparison-dropdown__item ${comparison === mode ? 'is-active' : ''}`}
							onClick={() => setMode(mode)}
						>
							<span>{MODE_LABELS[mode]}</span>
							{comparison === mode && <Check size={14} className="comparison-dropdown__check" />}
						</button>
					))}

					{/* Inline custom-period date picker — only visible after the user
					    picks "Custom period". Sits flush inside the dropdown so the
					    interaction feels continuous. */}
					{showCustomInline && (
						<div className="comparison-dropdown__custom">
							<div className="comparison-dropdown__field">
								<span>Start</span>
								<DatePickerInput
									value={customStart}
									onChange={setCustomStart}
									placeholder="Pick start"
									max={customEnd || undefined}
								/>
							</div>
							<div className="comparison-dropdown__field">
								<span>End</span>
								<DatePickerInput
									value={customEnd}
									onChange={setCustomEnd}
									placeholder="Pick end"
									min={customStart || undefined}
								/>
							</div>
							<button
								type="button"
								className="comparison-dropdown__apply"
								disabled={!customStart || !customEnd}
								onClick={applyCustomDates}
							>
								Apply
							</button>
						</div>
					)}

					<div className="comparison-dropdown__divider" />

					{/* Alignment modifiers — radio-like behavior: exactly one is "on".
					    `day_of_week` is the default (omitted from the URL), so a missing
					    `comparison_match` param is treated as `day_of_week`. */}
					<button
						type="button"
						className={`comparison-dropdown__item ${comparisonMatch === 'day_of_week' ? 'is-active' : ''}`}
						onClick={() => setMatch('day_of_week')}
					>
						<span>Match day of week</span>
						{comparisonMatch === 'day_of_week' && <Check size={14} className="comparison-dropdown__check" />}
					</button>
					<button
						type="button"
						className={`comparison-dropdown__item ${comparisonMatch === 'exact_date' ? 'is-active' : ''}`}
						onClick={() => setMatch('exact_date')}
					>
						<span>Match exact date</span>
						{comparisonMatch === 'exact_date' && <Check size={14} className="comparison-dropdown__check" />}
					</button>
				</div>
			)}
		</div>
	);
};

export default ComparisonPicker;
